import { useLayoutEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { SkipToContent } from '@krakentech/coral';

import GlobalMessage from '@/components/GlobalMessage';
import Header from '@/components/Header';
import { useAuth } from '@/components/Providers/Auth';
import { useSyncFeatureFlag } from '@/hooks/utils/useFeatureFlags';
import CustomHead from '@/pages/_head.page';
import { SkipToContentOverride } from '@/styles/Overrides.styled';
import { FeatureNames } from '@/types/features';
import { INTERNAL_PATHS } from '@/utils/urls';

import MasqueradeBanner from '../../auth/MasqueradeBanner';
import CookiePreferences from '../../CookiePreferences';
import Footer from '../../Footer';

import { StyledPageLayout } from './index.styled';

type PageLayoutProps = {
  children: React.ReactNode;
};

const PageLayout = ({ children }: PageLayoutProps) => {
  const { route, pathname } = useRouter();
  const { masqueradeToken, isAuthenticated } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);

  const loginReworkEnabled = useSyncFeatureFlag(FeatureNames.LoginRework);

  const { HOME, LOGIN, EXPIRED_LOGIN, ADDITIONAL_LOGIN_OPTIONS } =
    INTERNAL_PATHS;

  useLayoutEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.display = 'block';
    }
  }, []);

  // Redesign of the login flow is enabled when the user is on a login-related page
  const isLoginReworkEnabled =
    loginReworkEnabled &&
    ([LOGIN.path, EXPIRED_LOGIN.path, ADDITIONAL_LOGIN_OPTIONS.path].some(
      (path) => pathname === path
    ) ||
      (pathname === HOME.path && !isAuthenticated));

  return (
    <>
      {/* This is put in place because the document.title kept dropping off in some instance when using analytics */}
      <CustomHead />
      {/* Since we are routing any nonexisting pages to the custom 404.tsx page,
                conditionally rendering the SkipToContent component avoids the following issue:
                Warning: Prop `href` did not match. Server: "/404#maincontent" Client: "/test#maincontent" */}
      {route !== '/404' && (
        <SkipToContentOverride>
          <SkipToContent id="maincontent" />
        </SkipToContentOverride>
      )}

      {!isLoginReworkEnabled && <Header />}
      {masqueradeToken && <MasqueradeBanner />}

      <StyledPageLayout id="maincontent">
        {!isLoginReworkEnabled && <GlobalMessage />}
        <div ref={containerRef} style={{ display: 'none' }}>
          {children}
        </div>
      </StyledPageLayout>
      <CookiePreferences />
      <Footer />
    </>
  );
};

export default PageLayout;
